import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import { useQuery } from '../../../contexts/RouterContext';
import { icons } from '../../generic/Icon';
import Loader from '../../generic/Loader';
import TooltipIcon from '../../generic/TooltipIcon';
import { useRemediationPreviewURL } from '../hooks/useRemediationPreviewURL';
import { useLoadPreview } from '../preview/hooks';
import { DEFAULT_REMEDIATION_FORMAT, type ReportsV3QueryParams } from '../types';

import previewStyles from '../preview/Preview.module.less';

/**
 * Renders the preview iframe for the remediation guidance report.
 * Reuses styles from the shared Preview component.
 */
export const RemediationGuidancePreview: VoidFunctionComponent = () => {
  const [query] = useQuery<ReportsV3QueryParams>();
  const isPDF = (query.format ?? DEFAULT_REMEDIATION_FORMAT) === 'PDF';
  const previewURL = useRemediationPreviewURL();
  const { cacheBustedURL, debouncedPreviewURL, isLoading, isPending, onIframeLoad } = useLoadPreview(previewURL);

  return (
    <div className={previewStyles.preview}>
      <header className={previewStyles.header}>
        <span className={previewStyles.headerTitle}>
          Preview
        </span>
        <TooltipIcon
          id="remediation-preview-tooltip"
          icon={icons.circleQuestion}
          tooltip="Previews use sample data and only show a limited number of dependencies"
        />
      </header>
      {isLoading && (
        <div className={previewStyles.loading}>
          <Loader />
          <span className={previewStyles.loadingText}>
            Your sample report is being generated...
          </span>
        </div>
      )}
      <iframe
        key={cacheBustedURL}
        src={debouncedPreviewURL}
        title="remediation-guidance-preview"
        onLoad={onIframeLoad}
        className={cx(previewStyles.iframe, (isLoading || isPending) && previewStyles.iframeLoading)}
        sandbox={isPDF ? undefined : 'allow-same-origin allow-scripts'}
      />
    </div>
  );
};
